<template>
  <div v-easyadmin="easyadminId" ai-interview-component :style="theme" class="position-relative">
    <ai-monitor 
      v-if="monitorEnabled && viewMonitorSx"
      side="sx"
      :background="content.background.sx"
      :content="content"
      :opt-container-content="optContainerContent"
    ></ai-monitor>
    <ai-monitor
      v-else-if="monitorEnabled && viewMonitorDx"
      side="dx"
      :background="content.background.dx"
      :content="content"
      :opt-container-content="optContainerContent"
    ></ai-monitor>
    <ai-buttons-console 
      v-else-if="viewConsole"
      :content="content"
      :opt-container-content="optContainerContent"
    ></ai-buttons-console>
    <template v-else>
      <ai-face-recognition
        :class="{'debug': debugMode}"
        v-if="faceRecognitionEnabled"
        :inactive-time="content.face_recognition.inactive_time"
        :threshold="Number(content.face_recognition.threshold)"
        :socket-url="content.face_recognition.socket_url"
        @face-found="handleFaceFound"
        @face-not-found="handleFaceNotFound"
      ></ai-face-recognition>
      <!-- START INIT STEP -->
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <div v-if="activeStep===STEPS.COVER" class="step-init">
          <div class="position-absolute-center h-100vh d-flex flex-column justify-content-center align-items-center">
            <f-video ref-prefix="cover" :src="getVideo(activeStep)" class="h-100 w-100" muted autoplay loop webkit-playsinline playsinline></f-video>
          </div>
          <div @click="updateStep(STEPS.BOOKING)" class="cursor-pointer position-absolute w-100 h-100 bg-contain-center"></div>
        </div>
      </transition>
      <!-- END INIT STEP -->
      <!-- START BOOKING STEP -->
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <div v-if="activeStep===STEPS.BOOKING" class="step-booking">
          <div class="position-absolute-center overflow-hidden" :class="{'cursor-pointer': !enableBooking}" @click="handleBookingClick">
            <f-video ref-prefix="booking" :src="getVideo(activeStep)" class="h-100 w-100" autoplay webkit-playsinline playsinline loop></f-video>
          </div>
          <div class="step-booking__info position-absolute d-flex justify-content-center align-items-center w-100">
            <div class="row">
              <div class="col-12 subtitle text-center">
                <template v-if="enableBooking">
                  <p v-if="enableBooking">{{ft("ai.interview.booking")}}</p>
                  <img v-if="content.booking_image" class="img-fluid" :src="content.booking_image.url">
                </template>
                <p v-else>{{ft("ai.interview.start")}}</p>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <!-- END BOOKING STEP -->
      <!-- START INTRO STEP -->
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <div v-if="activeStep===STEPS.INTRO" class="position-absolute-center overflow-hidden">
          <f-video ref-prefix="intro" @ended="startExperience" :src="getVideo(activeStep)" class="h-100 w-100" autoplay webkit-playsinline playsinline></f-video>
        </div>
      </transition>
      <!-- END INTRO STEP -->
      <!-- START INTERVIEW STEP -->
      <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
        <div v-show="isInterviewStep" class="position-relative h-100vh overflow-hidden bg-black" :class="{'d-flex': isInterviewStep}">
          <div class="main-container position-relative">
            <div class="position-absolute-center overflow-hidden">
              <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
                <f-video ref-prefix="host" v-if="hostActive" :src="content.host_video.url" class="h-100 w-100" loop webkit-playsinline playsinline></f-video>
              </transition>
            </div>
            <div class="container-fluid interview-content p-0" :class="{'ai-steps': [STEPS.AI_TALK, STEPS.AI_TALK_WAITING].includes(activeStep)}" :style="theme">
              <div class="position-absolute-center">
                <f-video 
                  v-if="hasBgVideo"
                  :src="content.background.main.url" 
                  class="bg-video h-100 w-100" 
                  muted autoplay loop webkit-playsinline playsinline
                ></f-video>
              </div>
              <ai-avatar 
                v-if="avatar"
                v-model="avatar"
                :background="content.background.main"
                :audio-url="hostAudioURL"
                :visemes="visemes"
                :emotion="emotion"
                :environment-image="content.environment_image"
                :force-resize="isInterviewStep"
                @playing="handleSmilePlaying"
              ></ai-avatar>
              <ai-speech-smile
                v-else
                :audio-url="hostAudioURL"
                :emotion="emotion"
                :content="content"
                @playing="handleSmilePlaying"
              ></ai-speech-smile>
              <div class="host-content w-100 position-absolute-center">
                <template v-if="preMedia || activeStep===STEPS.AI_LOADING">
                  <div v-if="preMedia" class="intro-attachment overflow-hidden h-100 w-100 p-3 bg-black">
                    <f-video ref-prefix="preMedia" :src="preMedia" class="h-100 w-100" @ended="handlePreMediaEnded" autoplay></f-video>
                  </div>
                  <template v-else>
                    <div v-if="getVideo(activeStep)" class="overflow-hidden h-100 w-100">
                      <f-video ref-prefix="aiLoading" :src="getVideo(activeStep)" class="h-100 w-100" loop autoplay></f-video>
                    </div>
                    <div class="ai-thinking">
                      <div class="bubble-loading_content d-flex align-items-center justify-content-center">
                        <i class="fa-lg fa-solid fa-circle fa-bounce" style="--fa-animation-delay: 0s"></i>
                        <i class="fa-lg fa-solid fa-circle fa-bounce mx-2 mx-lg-3" style="--fa-animation-delay: 0.25s"></i>
                        <i class="fa-lg fa-solid fa-circle fa-bounce" style="--fa-animation-delay: 0.50s"></i>
                      </div>
                      <p class="text-uppercase text-center">{{ft("ai.interview.analyze")}}</p>
                    </div>
                  </template>
                </template>
                <template v-else-if="!hostActive">
                  <div class="host-listening">
                    <p v-if="[STEPS.AI_TALK, STEPS.AI_TALK_END].includes(activeStep)" class="text-uppercase text-center">{{ft("ai.interview.press")}}</p>
                    <p v-else-if="activeStep===STEPS.USER_TALK" class="text-uppercase text-center">{{ft("ai.interview.listening")}}</p>
                    <div class="row d-flex justify-content-center">
                      <div class="col-12 col-md-6 p-0">
                        <div class="guest-content">
                          <ai-speech 
                            v-if="activeStep!==STEPS.AI_LOADING"
                            :audio="content.recording_audio"
                            :enable-voice-recognition="!faceRecognitionEnabled || content.enable_voice_recognition"
                            :min-decibel="content.audio_min_decibel"
                            :line-width="2"
                            line-color="white"
                            :show-microphone="true"
                            :immediate="content.auto_mic || faceRecognitionEnabled || backPressed"
                            :start-recording="talk"
                            @start-recording="updateStep(STEPS.USER_TALK)"
                            @stop-recording="handleStopRecording"
                          >
                            <template #icon-content={toggleRecord}>
                              <i @click="toggleRecord" class="fa fa-lg fa-microphone fa-solid"></i>
                            </template>
                          </ai-speech>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
              <div class="transcription-container row d-flex justify-content-center w-100 position-absolute">
                <div class="col-12 col-md-6">
                  <div ref="transcriptionEl" class="transcription position-relative">
                    <p v-if="transcription.length>0" class="mb-0 transcription__text text-center w-100">{{transcriptionToAnimate}}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!monitorEnabled" class="media-container d-flex justify-content-end align-items-center active p-3" :class="{'active': media.url.length>0}">
            <img v-if="media.type === 'image'" class="img-fluid rounded" :src="media.url">
            <f-video v-if="media.type === 'video'" :src="media.url" class="h-100 w-100 rounded" autoplay muted webkit-playsinline playsinline></f-video>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { fandomBasicMixin, fandomExtendedMixin } from 'src/modules/fandom_mixins_module.js';
import { ActionCableComponentsMixin } from 'src/modules/websocket_module.js';
import mixerModule from 'src/modules/mixer_module.js';
import { createChat, textToVisemes, textToSpeech, audioToVisemes, fetchQuestion, transcribeAudio, startInterview, endInterview } from 'src/modules/services/ai-service';
import { TEXT_TO_SPEECH_SERVICES } from 'src/modules/utils/constants';
import FVideo from 'f-video';
import { AiMonitor, AiButtonsConsole, AiSpeechSmile, AiAvatar, AiFaceRecognition, AiSpeech } from 'ai';
import { v4 as uuidv4 } from 'uuid';

const STEPS = {
  COVER: "cover",
  BOOKING: "booking",
  INTRO: "intro",
  AI_LOADING: "ai_loading",
  AI_TALK: "ai_talk",
  AI_TALK_WAITING: "ai_talk_waiting",
  AI_TALK_END: "ai_talk_end",
  USER_TALK: "user_talk"
}

const TRANSCRIPTION_TYPE = {USER: "user", AI: "ai", NONE: "none"}

export default {
  mixins: [ fandomBasicMixin, fandomExtendedMixin, ActionCableComponentsMixin, mixerModule ],
  components: { FVideo, AiMonitor, AiButtonsConsole, AiSpeechSmile, AiAvatar, AiFaceRecognition, AiSpeech },
  data() {
    return {
      interviewChat: null,
      interviewStatus: 'waiting',
      hostActive: true,
      hostAudioBlob: null,
      hostAudioURL: null,
      visemes: [],
      emotion: 'neutral',
      transcriptionType: '',
      transcription: '',
      transcriptionToAnimate: '',
      isTrascripting: false,
      stream: null,
      talk: false,
      init: false,
      activeStep: STEPS.COVER,
      STEPS,
      scriptIndex: 0,
      mediaAttachment: null,
      media: {type: '', url: ''},
      preMedia: null,
      timeoutId: null,
      backPressed: false,
      side: 'main',
      chatInteractions: 0,

      // AI response stream
      eventSource: null,
      ttsQueue: [],
      firstChunk: true,
      chunkMessage: "",
      streamingAiResponse: false
    };
  },
  mounted() {
    Fandom.toggleHeaderFooter();
    if (this.isMainView) {
      this.initSubscriptions();

      if (this.monitorEnabled) {
        this.initMixerSubscription();
      }
    }
    
    if (!this.viewConsole) {
      if (this.isSafari) {
        this.stream = navigator.mediaDevices.getUserMedia({audio: true});
      }

      this.initChatInterview();
      if (!this.faceRecognitionEnabled) {
        $(document).on("keydown", this.handleKeyPress);
        this.subscribe(
          { channel: "ListenerChannel", room: "interview_console" },
          { received: this.handleConsoleMessage}
        );
      }
    }
  },
  beforeDestroy() {
    $(document).off("keydown", this.handleKeyPress);
  },
  methods: {
    playVideo() {
      this.$refs[`${this.activeStep}fVideo`]?.play();
    },
    handleKeyPress(e) {
      const type = 
        e.key == "ArrowUp" ? "start" : 
        e.key == "ArrowLeft" ? "back" : 
        e.key == "ArrowDown" ? "reset" : 
        e.key == "ArrowRight" ? "talk" : "";

      this.handleConsoleMessage({type});
    },
    updateStep(step, data={}) {
      this.activeStep = step;
      if (this.monitorEnabled) {
        this.sendMixerMessage('update-step', {step, ...data});
      }
    },
    handleFaceFound() {
      console.log("face found", this.activeStep)
      this.activeStep === this.STEPS.BOOKING && this.handleBookingClick();
    },
    handleFaceNotFound() {
      this.isInterviewStep && this.reset();
    },
    handleBookingClick() {
      if (!this.enableBooking) {
        const introVideo = this.getVideo(this.STEPS.INTRO);
        introVideo ? this.updateStep(this.STEPS.INTRO) : this.startExperience();
      }
    },
    handleConsoleMessage(data) {
      switch (data.type) {
        case "start":
          this.activeStep === this.STEPS.BOOKING && this.handleBookingClick();
          break;
        case "talk":
          if (this.isInterviewStep && !this.hostActive) {
            this.talk = !this.talk;
          }
          break;
        case "back":
          this.isInterviewStep && this.handleBack();
          break;
        case "reset":
          this.reset();
          break
        default:
          break;
      }
    },
    handleBack() {
      if (this.preMedia && !this.backPressed) {
        this.backPressed = true;
        this.goToPrevQuestion();
      }
    },
    initSubscriptions() {
      this.subscribe(
        { channel: "PublicChannel", room: this.containerContent.name, lang: globalState.lang },
        { received: (data) => this.receivedChannelData(this.containerContent.name, data) }
      );
    },
    updateInterviewSubscription() {
      this.subscribe(
        { channel: "ListenerChannel", room: this.interviewChat.name },
        { received: (data) => {
          this.interviewStatus = data.status;
          if (this.isEnded) {
            this.reset();
          }
        }}
      );
    },
    receivedChannelData(contentName, receivedData) {
      if (receivedData) {
        Fandom.getContentsCallback(contentName, receivedData, (data) => {
          const newContent = data.name_to_content[data.main_content_name];
          Vue.set(globalState.pageModel.name_to_content, data.main_content_name, newContent);
        });
      }
    },
    reset() {
      this.chatInteractions = 0;
      this.media = {type:'', url:''};
      this.preMedia = null;
      this.interviewStatus = "waiting";
      this.stopStreaming();
      this.ttsQueue = [];
      this.chunkMessage = "";
      this.isTrascripting = false;
      this.transcriptionType = TRANSCRIPTION_TYPE.NONE;
      this.transcription = '';
      this.updateStep(this.STEPS.BOOKING);
      window.clearTimeout(this.animateTranscriptionId);
      this.scriptIndex = 0;
      this.hostActive = true;
      this.deleteSubscription("ListenerChannel", this.interviewChat.name);
      this.initChatInterview();
    },
    async initChatInterview() {
      try {
        await this.createChat();
        await this.startInterview();
        const audioData = await this.generateAudio(this.ttsQueue[0].text);
        this.ttsQueue[0] = {...this.ttsQueue[0], ...audioData};
      } catch (error) {
        console.log(`%cRESET - ${error}`, "background-color: red;");
        this.reset();
      }
    },
    startExperience() {
      this.interviewStatus = 'started';
      this.updateStep(STEPS.AI_TALK_WAITING);
      this.processTTSQueue();
    },
    async createChat() {
      const response = await createChat(this.content.id, this.content.name);
      const data = await response.json();
      this.interviewChat = data.chat;
      this.updateInterviewSubscription();
    },
    async startInterview() {
      const body = {chat: this.interviewChat.name, content_cache_id: this.content.id};
      if (this.activeScript) {
        body.input = JSON.stringify({...this.activeScript});
        this.scriptIndex++;
      }
      const response = await Fandom.retry(()=>startInterview(body), 2, 500);
      const data = await response.json();
      if (data.status == 'queued' && data.jwt) {
        this.updateStep(this.STEPS.AI_TALK_WAITING);
        this.streamData(data.jwt);
      } else {
        this.ttsQueue.push({text: data.text.content});
      }
    },
    streamData(jwt) {
      // re-init stream status
      this.streamingAiResponse = true;
      this.firstChunk = true;
      this.chunkMessage = "";

      const url = Fandom.applyContextToUrl(`/api/v5/ai/stream-chat?jwt=${jwt}`);
      this.eventSource = new EventSource(url);
      this.eventSource.onmessage = this.handleStreamEvent;
      this.eventSource.onerror = this.stopStreaming;
    },
    async handleStreamEvent(event) {
      const data = JSON.parse(event.data);
      if (data.starter == true) {
        this.setMediaFromUrl(this.mediaAttachment ?? data.attachment);
        console.log("context:", data.context);
      } else if (data.error || data.finish_reason == "stop") {
        this.stopStreaming();
        this.chatInteractions++;
      }
      
      this.chunkMessage += data.message ?? "";
      if (
        (this.chunkMessage.length > 20 && this.chunkMessage.trim().endsWith(".")) ||
        (data.finish_reason == "stop" && this.chunkMessage.length > 0)
      ) {
        this.handleNewChunk();
      }
    },
    async handleNewChunk() {
      const text = this.chunkMessage;
      this.chunkMessage = "";

      const uuid = uuidv4();
      this.ttsQueue.push({uuid, text});
      const audioData = await this.generateAudio(text);
      const index = this.ttsQueue.findIndex(item => item.uuid === uuid);
      this.ttsQueue[index] = {...this.ttsQueue[index], ...audioData};

      console.log(this.activeStep, index, uuid, this.transcriptionType);
      if (this.activeStep === STEPS.AI_LOADING && index===0 && this.transcriptionType!==TRANSCRIPTION_TYPE.USER) {
        console.log("PROCESS QUEUE", this.activeStep, index, uuid, this.transcriptionType)
        this.updateStep(STEPS.AI_TALK_WAITING);
        this.processTTSQueue();
      }
    },
    stopStreaming() {
      this.eventSource?.close();
      this.streamingAiResponse = false;
    },
    async generateAudio(text) {
      const result = {audio: null, visemes: []};
      result.audio = await this.runTTS(text);
      if (this.avatar) {
        result.visemes = await this.generateVisemes(text, result.audio);
      }

      return result;
    },
    processTTSQueue() {
      if (this.activeStep === STEPS.AI_TALK_WAITING && this.ttsQueue.length > 0) {
        let nextChunk = this.ttsQueue[0];
        if (nextChunk.audio && (!this.avatar || nextChunk.visemes)) {
          // take the first chunk in queue and trigger ai speech
          nextChunk = this.ttsQueue.shift();
          this.hostAudioBlob = nextChunk.audio;
          this.transcription = nextChunk.text;
          this.visemes = nextChunk.visemes;
          this.aiTalk();
        } else {
          setTimeout(this.processTTSQueue, 500);
        }
      }
    },
    async runTTS(text) {
      try {
        const speechResponse = await Fandom.retry(()=>textToSpeech(text, this.content.synthesizer), 2, 500);
        const stream = await speechResponse.blob();
        const blob = new Blob([stream], { type: 'audio/wav' });

        return blob;
      } catch (error) {
        console.log(`%cRESET - ${error}`, "background-color: red;");
        this.reset();
      }
    },
    async generateVisemes(text, audio) {
      if (this.ttsService === TEXT_TO_SPEECH_SERVICES.POLLY) {
        const visemesResponse = await textToVisemes(text, this.content.synthesizer);
        const response = await visemesResponse.json();
        return response.visemes;
      } else {
        const fileAudio = new File([audio], "audio.mp3", {type: 'audio/mp3'});
        const visemesResponse = await audioToVisemes(fileAudio);
        const response = await visemesResponse.json();
        return response.visemes.map(v => {
          return {...v, time: v.start*1000 }
        });
      }
    },
    async handleStopRecording(blobAudio) {
      this.talk = false;
      const ovverideLastMessage = this.backPressed;
      try {
        this.media = {type:'', url:''};
        
        if (this.hasScript) {
          this.interviewStatus = this.isLastScriptQuestion ? "ended" : "started";
          if (!this.isLastScriptQuestion) {
            this.updateStep(this.STEPS.AI_LOADING);
            const introAttachmentUrl = this.content.script[this.scriptIndex]?.intro_attachment?.url;
            this.preMedia = introAttachmentUrl || null;
          } else {
            this.preMedia = null;
          }
        } else {
          this.updateStep(this.STEPS.AI_LOADING);
        }
        
        await this.trascribeAudio(blobAudio);
        if (!this.isEnded) {
          // write user transcription if no pre media
          this.transcriptionType = TRANSCRIPTION_TYPE.USER;
          !this.preMedia && this.writeTranscription(`“${this.transcription}”`);
          this.isTrascripting = true;

          this.fetchQuestion(ovverideLastMessage);
        } else {
          await endInterview(this.interviewChat.name, this.transcription);
          this.reset();
        }
      } catch (error) {
        console.log(`%cRESET - ${error}`, "background-color: red;");
        this.reset();
      }
    },
    handlePreMediaEnded() {
      this.preMedia=null;
      this.isTrascripting = false;
    },
    async trascribeAudio(blob) {
      const fileObject = new File([blob], "audio.mp4", {type: 'audio/mp4'});
      const body = new FormData();
      body.append("audio", fileObject);
      body.append("service", this.content.transcription?.service ?? "open_ai");
      body.append("keywords", this.content.transcription?.keywords ?? "");
      
      const response = await Fandom.retry(()=>transcribeAudio(body), 2, 500);
      const data = await response.json();
      this.transcription = data.transcription;
    },
    async fetchQuestion(ovverideLastMessage=false) {
      const body = {
        chat: this.interviewChat.name,
        content_cache_id: this.content.id,
        user_message: this.transcription,
        override_last_message: ovverideLastMessage
      };

      // reset back button feature
      if (this.hasScript && this.preMedia) {
        this.backPressed = false;
      }

      if (this.activeScript) {
        const scriptInput = {...this.activeScript};
        body.input = JSON.stringify(scriptInput);
        this.scriptIndex++;
        this.mediaAttachment = body.input?.attachment;
      }

      const response = await Fandom.retry(()=>fetchQuestion(body), 2, 500);
      const data = await response.json();

      if (data.status == 'queued' && data.jwt) {
        this.streamData(data.jwt);
      }
    },
    setMediaFromUrl(url) {
      if (url) {
        const key = Fandom.isImage(url) ? "image" : "video";
        this.media = {type: key, url};
      }
    },
    aiTalk() {
      if (this.backPressed) {
        // if user pressed back we don't have to go to next question
        return;
      }
      
      //this.transcriptionType = TRANSCRIPTION_TYPE.AI;
      this.transcriptionToAnimate = '';
      this.hostAudioURL = window.URL.createObjectURL(this.hostAudioBlob);
      this.hostAudioBlob = null;
      this.updateStep(this.STEPS.AI_TALK, this.media.url ? {media: this.media} : {});
      this.hostActive = true;
    },
    goToPrevQuestion() {
      if (this.hasScript) {
        this.scriptIndex--;
        this.interviewStatus = "started";
        this.hostActive = false;
        this.talk = true;
        this.preMedia = null;
        this.endTranscription();
      }
    },
    handleSmilePlaying(duration) {
      this.transcriptionType = TRANSCRIPTION_TYPE.AI;
      this.writeTranscription(this.transcription.trim(), duration);
    },
    writeTranscription(text, duration=0) {
      this.isTrascripting = true;
      let words = [];
      let timeoutS = 0.1;
      if (this.transcriptionType == TRANSCRIPTION_TYPE.AI && duration>0) {
        words = text.match(/[^ ]+ ?| /g);
        timeoutS = duration/words.length;
      } else {
        let i = 0;
        while (i < text.length) {
          let charsToPrint = Math.floor(Math.random() * 6);
          let to = i+charsToPrint < text.length ? i+charsToPrint : text.length
          words.push(text.substring(i, to));
          i += charsToPrint;
        }
      }
      this.animateTranscription(words, timeoutS, 0);
    },
    animateTranscription(words, timeoutS=0.1, index=0) {
      if (index < words.length) {
        this.$refs[`transcriptionEl`]?.classList.add("blink-animation");
        this.transcriptionToAnimate += words[index];
        index++;
        this.animateTranscriptionId = setTimeout(() => this.animateTranscription(words, timeoutS , index), timeoutS*1000);
      } else {
        this.$refs[`transcriptionEl`]?.classList.remove("blink-animation");
        if (this.isEnded) {
          setTimeout(this.reset, 1000);
        } else {
          this.endTranscription();
        }
      }
    },
    endTranscription() {
      this.hostAudioURL = null;
      if (this.transcriptionType == TRANSCRIPTION_TYPE.AI) {
        if (this.ttsQueue.length > 0) {
          // next chunk ready for tts
          this.updateStep(STEPS.AI_TALK_WAITING);
          this.processTTSQueue();
          return;
        } else if (this.streamingAiResponse) {
          // queue is empty but streaming not ended, waiting for next chunk
          this.updateStep(STEPS.AI_TALK_WAITING);
          return;
        } else {
          setTimeout(()=>{
            this.updateStep(STEPS.AI_TALK_END);
            this.hostActive = !this.hostActive;
            this.resetTranscription();
          }, 1000);
        }
      } else if (this.ttsQueue.length > 0) {
        this.resetTranscription();
        // next chunk ready for tts
        this.updateStep(STEPS.AI_LOADING);
        this.processTTSQueue();
      } else {
        setTimeout(()=>{
          this.resetTranscription();
          if (this.ttsQueue.length > 0) {
            this.updateStep(STEPS.AI_LOADING);
            this.processTTSQueue();
          }
        }, 2000);
      }
    },
    resetTranscription() {
      this.transcriptionType = TRANSCRIPTION_TYPE.NONE;
      this.isTrascripting = false;
      this.transcription = '';
      this.transcriptionToAnimate = '';
    },
    getVideo(type) {
      return this.content.monitors[type]?.[this.side]?.url ?? null;
    }
  },
  computed: {
    isChat() {
      return this.content.conversation_type === "chat";
    },
    isInterviewStep() {
      return [STEPS.AI_TALK, STEPS.AI_TALK_WAITING, STEPS.AI_LOADING, STEPS.USER_TALK, STEPS.AI_TALK_END].includes(this.activeStep);
    },
    enableBooking() {
      return this.content.enable_booking ?? false;
    },
    viewConsole() {
      return new URLSearchParams(window.location.search).has('console') && !this.faceRecognitionEnabled;
    },
    monitorEnabled() {
      return this.content.enable_monitors ?? false;
    },
    isMainView() {
      return !this.monitorEnabled || (!this.viewMonitorSx && !this.viewMonitorDx);
    },
    viewMonitorSx() {
      return new URLSearchParams(window.location.search).has('monitor-sx');
    },
    viewMonitorDx() {
      return new URLSearchParams(window.location.search).has('monitor-dx');
    },
    debugMode() {
      return new URLSearchParams(window.location.search).has('debug');
    },
    isEnded() {
      return (!this.isChat && this.interviewStatus=="ended") || (this.isChat && this.chatInteractions>=this.content.chat_mode_interactions);
    },
    themeType() {
      // override fandom mixin computed prop
      return "dark";
    },
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    },
    ttsService() {
      return this.content.synthesizer?.service ?? TEXT_TO_SPEECH_SERVICES.POLLY;
    },
    hasScript() {
      return this.content.script?.length > 0;
    },
    isLastScriptQuestion() {
      return this.scriptIndex === this.content.script.length;
    },
    activeScript() {
      if (this.hasScript && this.scriptIndex < this.content.script.length) {
        const currentScript = this.content.script[this.scriptIndex];
        return {
          content: currentScript.prompt,
          attachment: currentScript.attachment?.url,
          role: "system"
        };
      }

      return null;
    },
    avatar() {
      return this.content.avatar_model?.url;
    },
    hasBgVideo() {
      return this.content.background?.main?.url && !Fandom.isImage(this.content.background.main.url);
    },
    // WATCHER SUPPORT COMPUTED PROPERTIES
    hasToGoToNextQuestion() {
      return this.isInterviewStep && !this.isTrascripting && (this.activeStep==STEPS.AI_LOADING && this.preMedia==null);
    },
    faceRecognitionEnabled() {
      return !!this.content.face_recognition;
    }
  },
  watch: {
    hostAudioBlob(newVal, oldVal) {
      if (newVal && newVal!==oldVal && this.hasToGoToNextQuestion) {
        this.aiTalk();
      }
    },
    hasToGoToNextQuestion(newVal) {
      if (newVal && this.hostAudioBlob) {
        this.aiTalk();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
[ai-interview-component] {
  height: 100vh;
  color: var(--theme-color);

  ::v-deep {
    .face-recognition {
      visibility: hidden;
      z-index: -1;
      position: absolute;
      top: 0;
      right: 0;

      &.debug {
        z-index: 9;
      }
    }
  }

  .main-container {
    width: 100%;
    transition: all .4s ease-in-out;
  }

  .media-container {
    position: absolute;
    top: 0;
    right: -40vw;
    width: 40vw;
    z-index: 9;
    transition: all .4s ease-in-out;

    &.active {
      right: 0;
    }

    img {
      max-height: 40vh;
    }
  }

  .step-booking {
    &__info {
      bottom: 5vh;
      font-size: 2vh;

      img {
        width: 10vh;
      }
    }
  }

  .interview-content {
    height: 100%;
    overflow: hidden;
    color: var(--theme-color);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ::v-deep {
      [ai-avatar-content] {
        height: 100%;
        width: 100%;
        position: absolute !important;
        top: 0;
      }

      [ai-speech-smile-component] {
        position: absolute !important;
        top: 0;
      }
    }

    .host-content {
      .intro-attachment {
        video {
          max-height: 100vh;
          max-width: 100vw;
          border-radius: 15px;
        }
      }
    }

    .transcription {
      margin-top: 20vh;
      max-height: 14vh;
      min-width: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column-reverse;

      &::before {
        position: absolute;
        top: 0;
        height: 100%;
        width: 100%;
        content: "";
      }

      &.blink-animation {
        .transcription__text::after {
          animation: blink 1s steps(5,start) infinite;
          content: "_";
          margin-left: 0.25rem;
          vertical-align: baseline;

          @keyframes blink {
            to {
              visibility: hidden;
            }
          }
        }
      }

      p {
        font-size: 3vh;
        line-height: 1.2;
        font-weight: 600;
        width: 80%;
        margin-bottom: 0;
      }
    }

    .guest-content {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 7vh;

      ::v-deep [ai-speech] {
        .canvas-container {
          overflow: hidden;
        }

        .icon-container {
          width: 7vh;
          height: 7vh;
          background: black;
          margin-left: 0 !important;
          border: 2px solid white;
        }

        &.recording {
          .icon-container {
            position: absolute;
          }
        }

        .fa-microphone {
          font-size: 4vh;
          line-height: .04167em;
          vertical-align: -0.125em;
        }
      }
    }
  }

  .position-absolute-center {
    video {
      object-fit: cover;
    }
  }

  .ai-thinking {
    position: absolute;
    bottom: 10vh;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;

    p {
      font-size: 2vh;
      font-weight: 600;
      margin-top: 2.5vh;
      width: 40vw;
    }
  }

  .host-listening {
    position: absolute;
    bottom: 10vh;
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;

    p {
      width: 40vh;
      font-size: 2vh;
      font-weight: 600;
      margin-top: 2.5vh;
    }
  }

  .bubble-loading_content {
    display: inline-block;
    position: relative;

    .fa-circle {
      --fa-bounce-start-scale-x: 1;
      --fa-bounce-start-scale-y: 1;
      --fa-bounce-jump-scale-x: 1;
      --fa-bounce-jump-scale-y: 1;
      --fa-bounce-land-scale-x: 1;
      --fa-bounce-land-scale-y: 1;
      --fa-bounce-rebound: 0;
      --fa-fade-opacity: 0;
    }

    .fa-lg {
      line-height: 1;
    }
  }
}
</style>